import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

const CellStyle = {
  border: '1px solid lightgrey',
  padding: 8,
  overflow: 'hidden',
  minWidth: 0
};

const HeaderStyle = {
  ...CellStyle,
  fontWeight: 600
};

// prettier-ignore
const tryParseJSON = (message) => {
  try {
    return JSON.parse(message);
  } catch {//NOSONAR
    return null;
  }
};

type TwilioEvent = {
  event: 'VOICECB' | 'VIDEOCB' | 'RECEIVING_VIDEO';
  message: string;
  user_type_code: 'PATIENT' | 'CLINICIAN';
};

// Table to display a list of video media file URLs in the HBS S3 bucket. Includes a column indicating whether the
// media is complete as well as the participant that generated them.
export default function VideoMediaTable({ events }: { events: TwilioEvent[] }) {
  const { t } = useTranslation('common');
  const hookEvents = events
    .filter((e) => e.event === 'VOICECB' || e.event === 'VIDEOCB')
    .map((e) => ({ ...e, messageJSON: tryParseJSON(e.message) }));

  const participants = Object.fromEntries(
    events
      .filter((e) => e.event === 'RECEIVING_VIDEO')
      .map((m) => {
        const message = tryParseJSON(m.message);

        return [message.from.sid, m.user_type_code === 'CLINICIAN' ? 'PATIENT' : 'CLINICIAN'];
      })
  );
  const failedVideoMedia = [
    ...new Set<string>(
      events
        .filter((e) => e.event === 'VIDEOCB')
        .map((e) => tryParseJSON(e.message))
        .filter((m) => m?.StatusCallbackEvent === 'recording-failed')
        .map((m) => m.RecordingUri)
    )
  ];
  const completedVideoMedia = [
    ...new Set<{ ParticipantSid: string; MediaExternalLocation: string }>(
      events
        .filter((e) => e.event === 'VIDEOCB')
        .map((e) => tryParseJSON(e.message))
        .filter((hookBody) => hookBody?.StatusCallbackEvent === 'recording-completed')
    )
  ];

  const findFirstVideoFailure = (uri: string) =>
    hookEvents
      .map((e) => e.messageJSON)
      .find((m) => m?.StatusCallbackEvent === 'recording-failed' && m?.RecordingUri === uri);

  const findVideoRecordingSucceeded = (uri: string) =>
    hookEvents
      .map((e) => e.messageJSON)
      .find((m) => m?.StatusCallbackEvent === 'recording-completed' && m?.RecordingUri === uri);

  const incompleteVideoMedia = [
    ...new Set<any>(
      hookEvents
        .filter(({ messageJSON: m }) => m?.StatusCallbackEvent === 'recording-started')
        .filter(
          ({ messageJSON: m }) => !findFirstVideoFailure(m.RecordingUri) && !findVideoRecordingSucceeded(m.RecordingUri)
        )
    )
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, max-content) minmax(0, max-content) minmax(0, 10fr)',
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        border: '1px solid lightgrey'
      }}
    >
      {/* Header */}
      <div style={{ display: 'contents' }}>
        <div style={HeaderStyle}>{t('completed')}</div>
        <div style={HeaderStyle}>{t('participant')}</div>
        <div style={HeaderStyle}>{t('media')}</div>
      </div>

      <div style={{ display: 'contents' }}>
        {completedVideoMedia.map(({ ParticipantSid, MediaExternalLocation }, i) => (
          <Fragment key={MediaExternalLocation}>
            <div style={CellStyle}>✅</div>
            <div style={CellStyle}>{participants[ParticipantSid]}</div>
            <div style={CellStyle}>{MediaExternalLocation}</div>
          </Fragment>
        ))}
        {failedVideoMedia.map((uri) => {
          const m = findFirstVideoFailure(uri);

          if (findVideoRecordingSucceeded(uri)) return null;

          return (
            <Fragment key={uri}>
              <div style={CellStyle}>❌</div>
              <div style={CellStyle}>{participants[m.ParticipantSid]}</div>
              <div style={CellStyle}>
                {m.RecordingUri}: {m.ErrorMessage}
              </div>
            </Fragment>
          );
        })}
        {incompleteVideoMedia.map(({ messageJSON: m }) => {
          return (
            <Fragment key={m.RecordingUri}>
              <div style={CellStyle}>⏳</div>
              <div style={CellStyle}>{participants[m.ParticipantSid]}</div>
              <div style={CellStyle}>
                {m.RecordingUri} {t('(incomplete)')}
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
